import { useState } from 'preact/hooks';
import { signal } from '@preact/signals';
import { startDownload, type DownloadScope } from '../storage/download-engine.js';
import { currentBible } from '../state/bible-store.js';
import { playerSettings } from '../state/player-settings.js';
import { route } from '../state/store.js';

type ScopeOption = {
  scope: DownloadScope;
  label: string;
  sub: string;
  size: string;
};

const SCOPE_OPTIONS: ScopeOption[] = [
  { scope: 'book', label: 'This book', sub: 'Current book only', size: '~12 MB' },
  { scope: 'nt', label: 'New Testament', sub: '27 books', size: '~180 MB' },
  { scope: 'full', label: 'Whole Bible', sub: '66 books', size: '~1.8 GB' },
];

const _open = signal(false);

export function useOfflinePrompt(): { open: () => void } {
  return {
    open: () => { _open.value = true; },
  };
}

export function OfflinePrompt() {
  const isOpen = _open.value;
  const [selectedScope, setSelectedScope] = useState<ScopeOption | null>(null);

  if (!isOpen) return null;

  const dismiss = (): void => {
    _open.value = false;
    setSelectedScope(null);
  };

  const onScrim = (e: MouseEvent): void => {
    if ((e.target as HTMLElement) === (e.currentTarget as HTMLElement)) dismiss();
  };

  const onSave = async (): Promise<void> => {
    if (!selectedScope) return;
    const { bibleId } = currentBible.peek();
    const audioStyle = playerSettings.peek().audioStyle;
    const currentBook = route.peek().book;
    dismiss();
    await startDownload(bibleId, audioStyle, selectedScope.scope, currentBook);
  };

  if (selectedScope) {
    return (
      <div class="modal-scrim" onClick={onScrim}>
        <div class="modal-sheet" role="dialog" aria-modal="true" aria-label="Confirm offline save">
          <div class="modal-sheet-grabber" />
          <div class="modal-sheet-header">
            <button
              type="button"
              class="modal-sheet-close"
              style={{ marginRight: 'auto' }}
              aria-label="Back"
              onClick={() => setSelectedScope(null)}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <span class="modal-sheet-title" style={{ flex: 1, textAlign: 'center' }}>
              Save {selectedScope.label}?
            </span>
            <button type="button" class="modal-sheet-close" onClick={dismiss} aria-label="Close">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          <div class="modal-sheet-body">
            <div class="offline-confirm-size">{selectedScope.size} · Audio + text</div>
            <p class="offline-confirm-note">
              Stored privately on your device. If you forget your PIN, this content cannot be recovered.
            </p>
            <div class="offline-confirm-actions">
              <button type="button" class="offline-btn-save" onClick={() => void onSave()}>
                Save offline
              </button>
              <button type="button" class="offline-btn-cancel" onClick={dismiss}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div class="modal-scrim" onClick={onScrim}>
      <div class="modal-sheet" role="dialog" aria-modal="true" aria-label="Save for offline">
        <div class="modal-sheet-grabber" />
        <div class="modal-sheet-header">
          <span class="modal-sheet-title">Save to this device</span>
          <button type="button" class="modal-sheet-close" onClick={dismiss} aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div class="modal-sheet-body">
          <p class="offline-sheet-sub">No internet needed once saved.</p>
          <div class="offline-scope-list">
            {SCOPE_OPTIONS.map((opt) => (
              <button
                key={opt.scope}
                type="button"
                class="offline-scope-opt"
                onClick={() => setSelectedScope(opt)}
              >
                <div class="offline-scope-main">
                  <span class="offline-scope-label">{opt.label}</span>
                  <span class="offline-scope-size">{opt.size}</span>
                </div>
                <span class="offline-scope-sub">{opt.sub}</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            ))}
          </div>
          <button type="button" class="offline-btn-cancel" onClick={dismiss}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
